
import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaTimes,
  FaSyncAlt,
  FaExclamationCircle,
  FaCheckCircle,
  FaClock,
  FaFolderOpen,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { decryptData } from '../../utils/storageCrypto';


const API_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET;

const CASE_TYPES = [
  "CANCELLATION",
  "RETURN",
  "REFUND",
  "DELIVERY_ISSUE",
  "SUPPLIER_RETURN",
];

const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const STATUSES = [
  "OPEN",
  "ASSIGNED",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "REJECTED",
];

const EMPTY_FORM = {
  case_number: "",
  case_type: "DELIVERY_ISSUE",
  description: "",
  priority: "MEDIUM",
  entity_type: "",
  entity_id: "",
  owner_id: "",
  status: "OPEN",
  remarks: "",
};

const statusColors = {
  OPEN: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-indigo-100 text-indigo-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  RESOLVED: "bg-green-100 text-green-700",
  CLOSED: "bg-gray-200 text-gray-700",
  REJECTED: "bg-red-100 text-red-700",
};

const priorityColors = {
  LOW: "bg-gray-100 text-gray-700",
  MEDIUM: "bg-blue-100 text-blue-700",
  HIGH: "bg-orange-100 text-orange-700",
  CRITICAL: "bg-red-100 text-red-700",
};

const formatLabel = (value = "") =>
  value
    .toString()
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (date) => {
  if (!date) return "—";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "—";

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getCaseId = (item) => item?._id || item?.id;

const normalizeCases = (data) => {
  // Supports common Express response structures.
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.cases)) return data.cases;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.cases)) return data.data.cases;
  return [];
};

export default function CasesList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [modal, setModal] = useState(null);
  // modal: { mode: "create" | "edit" | "view", item?: case }

  const [form, setForm] = useState(EMPTY_FORM);

  const [remarksDraft, setRemarksDraft] = useState("");

  const getHeaders = () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    return {
      "Content-Type": "application/json",
      ...(token ? { 'x-token': token } : {}),
    };
  };

  const apiRequest = async (path, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        ...getHeaders(),
        ...(options.headers || {}),
      },
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        result.message ||
          result.error ||
          `Request failed with status ${response.status}`
      );
    }

    return result;
  };

  const loadCases = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await apiRequest("returns/get-cases");
      setCases(normalizeCases(result));
    } catch (err) {
      setError(err.message || "Failed to load cases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, []);

  const filteredCases = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return cases.filter((item) => {
      const matchesSearch =
        !searchText ||
        [
          item.case_number,
          item.description,
          item.entity_type,
          item.entity_id,
          item.case_type,
          item.status,
          item.remarks,
        ].some((value) =>
          String(value || "").toLowerCase().includes(searchText)
        );

      const matchesStatus =
        statusFilter === "ALL" || item.status === statusFilter;

      const matchesPriority =
        priorityFilter === "ALL" || item.priority === priorityFilter;

      const matchesType =
        typeFilter === "ALL" || item.case_type === typeFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesType
      );
    });
  }, [cases, search, statusFilter, priorityFilter, typeFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, priorityFilter, typeFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCases.length / pageSize)
  );

  const visibleCases = filteredCases.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const summary = useMemo(
    () => ({
      total: cases.length,
      open: cases.filter((item) => item.status === "OPEN").length,
      inProgress: cases.filter(
        (item) => item.status === "IN_PROGRESS"
      ).length,
      resolved: cases.filter(
        (item) =>
          item.status === "RESOLVED" || item.status === "CLOSED"
      ).length,
    }),
    [cases]
  );

  const showMessage = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(""), 3000);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setError("");
    setModal({ mode: "create" });
  };

  const openEdit = (item) => {
    setForm({
      case_number: item.case_number || "",
      case_type: item.case_type || "DELIVERY_ISSUE",
      description: item.description || "",
      priority: item.priority || "MEDIUM",
      entity_type: item.entity_type || "",
      entity_id: item.entity_id || "",
      owner_id:
        typeof item.owner_id === "object"
          ? item.owner_id?._id || ""
          : item.owner_id || "",
      status: item.status || "OPEN",
      remarks: item.remarks || "",
    });

    setError("");
    setModal({ mode: "edit", item });
  };

  const openView = (item) => {
    setRemarksDraft(item.remarks || "");
    setError("");
    setModal({ mode: "view", item });
  };

  const closeModal = () => {
    if (!saving) setModal(null);
  };

  const updateForm = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const saveCase = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.description.trim()) {
      setError("Please enter a case description.");
      return;
    }

    if (modal.mode === "create" && !form.case_number.trim()) {
      setError("Please enter a case number.");
      return;
    }

    const payload = {
      ...form,
      case_number: form.case_number.trim(),
      description: form.description.trim(),
      entity_type: form.entity_type.trim(),
      entity_id: form.entity_id.trim(),
      owner_id: form.owner_id.trim() || null,
      remarks: form.remarks.trim(),
    };

    setSaving(true);

    try {
      if (modal.mode === "create") {
        await apiRequest("/cases", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        showMessage("Case created successfully.");
      } else {
        const id = getCaseId(modal.item);

        if (!id) throw new Error("Case ID is missing.");

        await apiRequest(`/cases/${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        showMessage("Case updated successfully.");
      }

      setModal(null);
      await loadCases();
    } catch (err) {
      setError(err.message || "Unable to save case.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (item, status) => {
    const id = getCaseId(item);
    if (!id) return;

    setError("");

    try {
      await apiRequest(`/cases/${id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });

      setCases((current) =>
        current.map((row) =>
          getCaseId(row) === id ? { ...row, status } : row
        )
      );

      if (modal?.mode === "view") {
        setModal((current) => ({
          ...current,
          item: { ...current.item, status },
        }));
      }

      showMessage(`Case marked ${formatLabel(status).toLowerCase()}.`);
    } catch (err) {
      setError(err.message || "Unable to update case status.");
    }
  };

  const saveRemarks = async () => {
    const item = modal?.item;
    const id = getCaseId(item);

    if (!id) return;

    setSaving(true);
    setError("");

    try {
      await apiRequest(`/cases/${id}`, {
        method: "PUT",
        body: JSON.stringify({ remarks: remarksDraft }),
      });

      setCases((current) =>
        current.map((row) =>
          getCaseId(row) === id
            ? { ...row, remarks: remarksDraft }
            : row
        )
      );

      setModal((current) => ({
        ...current,
        item: { ...current.item, remarks: remarksDraft },
      }));

      showMessage("Remarks saved.");
    } catch (err) {
      setError(err.message || "Unable to save remarks.");
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setPriorityFilter("ALL");
    setTypeFilter("ALL");
    setPage(1);
  };

  const exportCsv = () => {
    const columns = [
      "case_number",
      "case_type",
      "description",
      "priority",
      "entity_type",
      "entity_id",
      "owner_id",
      "status",
      "remarks",
      "createdAt",
    ];

    const escapeCsv = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const rows = [
      columns.join(","),
      ...filteredCases.map((item) =>
        columns
          .map((column) => {
            let value = item[column];

            if (column === "owner_id" && value && typeof value === "object") {
              value = value._id || value.name || "";
            }

            return escapeCsv(value);
          })
          .join(",")
      ),
    ];

    const blob = new Blob([rows.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "cases.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, icon, iconClass }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {value}
          </p>
        </div>
        <div className={`rounded-xl p-3 ${iconClass}`}>{icon}</div>
      </div>
    </div>
  );

  const Field = ({ label, children, required = false }) => (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );

  const inputClass =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-7">
      {/* Header */}
      <div className="mb-7 flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-lg font-bold tracking-tight text-gray-900">
            Cases Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, assign, track, and resolve customer and supplier cases.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadCases}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Refresh
          </button>

          <button
            onClick={exportCsv}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Export CSV
          </button>

          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-[#f7525a] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#f7525a]"
          >
            <FaPlus />
            Create Case
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && !modal && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <FaExclamationCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="ml-auto"
            aria-label="Dismiss error"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <FaCheckCircle />
          {success}
        </div>
      )}

      {/* Summary */}
      <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Cases"
          value={summary.total}
          icon={<FaFolderOpen className="text-xl" />}
          iconClass="bg-blue-50 text-blue-600"
        />
        <StatCard
          title="Open Cases"
          value={summary.open}
          icon={<FaExclamationCircle className="text-xl" />}
          iconClass="bg-indigo-50 text-indigo-600"
        />
        <StatCard
          title="In Progress"
          value={summary.inProgress}
          icon={<FaClock className="text-xl" />}
          iconClass="bg-amber-50 text-amber-600"
        />
        <StatCard
          title="Resolved / Closed"
          value={summary.resolved}
          icon={<FaCheckCircle className="text-xl" />}
          iconClass="bg-green-50 text-green-600"
        />
      </div>

      {/* Filters */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
          <FaFilter className="text-gray-400" />
          Search and Filters
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="relative xl:col-span-2">
            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search cases..."
              className={`${inputClass} pl-9`}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className={inputClass}
          >
            <option value="ALL">All Statuses</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {formatLabel(status)}
              </option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
            className={inputClass}
          >
            <option value="ALL">All Priorities</option>
            {PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {formatLabel(priority)}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className={inputClass}
          >
            <option value="ALL">All Case Types</option>
            {CASE_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            Showing {filteredCases.length} of {cases.length} cases
          </p>
          <button
            onClick={resetFilters}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Cases Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-4 font-semibold">Case</th>
                <th className="px-5 py-4 font-semibold">Type</th>
                <th className="px-5 py-4 font-semibold">Description</th>
                <th className="px-5 py-4 font-semibold">Priority</th>
                <th className="px-5 py-4 font-semibold">Owner</th>
                <th className="px-5 py-4 font-semibold">Status</th>
                <th className="px-5 py-4 font-semibold">Created</th>
                <th className="px-5 py-4 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-5 py-16 text-center text-gray-500">
                    Loading cases...
                  </td>
                </tr>
              ) : visibleCases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-5 py-16 text-center">
                    <FaFolderOpen className="mx-auto mb-3 text-3xl text-gray-300" />
                    <p className="font-medium text-gray-700">
                      No cases found
                    </p>
                    <p className="mt-1 text-gray-500">
                      Try changing your filters or create a new case.
                    </p>
                  </td>
                </tr>
              ) : (
                visibleCases.map((item) => {
                  const owner =
                    typeof item.owner_id === "object"
                      ? item.owner_id?.name ||
                        item.owner_id?.email ||
                        item.owner_id?._id
                      : item.owner_id;

                  return (
                    <tr
                      key={getCaseId(item)}
                      className="transition hover:bg-gray-50"
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-900">
                          {item.case_number || "—"}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          ID: {String(getCaseId(item) || "—").slice(-8)}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-gray-700">
                        {formatLabel(item.case_type || "")}
                      </td>

                      <td className="max-w-xs px-5 py-4">
                        <p className="truncate font-medium text-gray-800">
                          {item.description || "—"}
                        </p>
                        {item.entity_type && (
                          <p className="mt-1 truncate text-xs text-gray-500">
                            {formatLabel(item.entity_type)}: {item.entity_id || "—"}
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            priorityColors[item.priority] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {formatLabel(item.priority || "—")}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-gray-600">
                        {owner ? String(owner) : "Unassigned"}
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            statusColors[item.status] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {formatLabel(item.status || "OPEN")}
                        </span>
                      </td>

                      <td className="px-5 py-4 whitespace-nowrap text-gray-500">
                        {formatDate(item.createdAt || item.created_at)}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openView(item)}
                            title="View details"
                            className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-700"
                          >
                            <FaEye />
                          </button>

                          <button
                            onClick={() => openEdit(item)}
                            title="Edit case"
                            className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
              Previous
            </button>

            <button
              disabled={page >= totalPages}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Create / Edit / View Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {modal.mode === "create"
                    ? "Create New Case"
                    : modal.mode === "edit"
                    ? "Edit Case"
                    : "Case Details"}
                </h2>
                {modal.mode !== "create" && (
                  <p className="mt-1 text-sm text-gray-500">
                    {modal.item?.case_number || "Case"}
                  </p>
                )}
              </div>

              <button
                onClick={closeModal}
                disabled={saving}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {modal.mode === "view" ? (
              <div className="space-y-5 p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Case Number
                    </p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {modal.item?.case_number || "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Case Type
                    </p>
                    <p className="mt-1 text-gray-800">
                      {formatLabel(modal.item?.case_type || "")}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Priority
                    </p>
                    <p className="mt-1">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          priorityColors[modal.item?.priority] || ""
                        }`}
                      >
                        {formatLabel(modal.item?.priority || "—")}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Current Status
                    </p>
                    <p className="mt-1">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          statusColors[modal.item?.status] || ""
                        }`}
                      >
                        {formatLabel(modal.item?.status || "—")}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Related Entity
                    </p>
                    <p className="mt-1 text-gray-800">
                      {modal.item?.entity_type
                        ? `${formatLabel(modal.item.entity_type)} · ${
                            modal.item?.entity_id || "No ID"
                          }`
                        : "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Created
                    </p>
                    <p className="mt-1 text-gray-800">
                      {formatDate(
                        modal.item?.createdAt || modal.item?.created_at
                      )}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Description
                  </p>
                  <p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
                    {modal.item?.description || "No description"}
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Remarks / Internal Notes
                  </label>
                  <textarea
                    rows={4}
                    value={remarksDraft}
                    onChange={(event) => setRemarksDraft(event.target.value)}
                    placeholder="Add investigation notes, updates, or resolution details..."
                    className={inputClass}
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={saveRemarks}
                      disabled={saving}
                      className="rounded-lg bg-[#f7525a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#f7525a] disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Save Remarks"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Update Status
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((status) => (
                      <button
                        key={status}
                        disabled={saving || modal.item?.status === status}
                        onClick={() => updateStatus(modal.item, status)}
                        className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                          modal.item?.status === status
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {formatLabel(status)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
                  <button
                    onClick={() => openEdit(modal.item)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <FaEdit />
                    Edit Case
                  </button>
                  <button
                    onClick={closeModal}
                    className="rounded-lg bg-[#f7525a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#f7525a]"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={saveCase} className="space-y-5 p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Case Number" required>
                    <input
                      name="case_number"
                      value={form.case_number}
                      onChange={updateForm}
                      disabled={modal.mode === "edit"}
                      placeholder="Example: CASE-2026-001"
                      className={`${inputClass} disabled:bg-gray-100`}
                      required
                    />
                  </Field>

                  <Field label="Case Type" required>
                    <select
                      name="case_type"
                      value={form.case_type}
                      onChange={updateForm}
                      className={inputClass}
                      required
                    >
                      {CASE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {formatLabel(type)}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Priority" required>
                    <select
                      name="priority"
                      value={form.priority}
                      onChange={updateForm}
                      className={inputClass}
                      required
                    >
                      {PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {formatLabel(priority)}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Status" required>
                    <select
                      name="status"
                      value={form.status}
                      onChange={updateForm}
                      className={inputClass}
                      required
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {formatLabel(status)}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Entity Type">
                    <input
                      name="entity_type"
                      value={form.entity_type}
                      onChange={updateForm}
                      placeholder="Example: ORDER, CUSTOMER, SUPPLIER"
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Entity ID">
                    <input
                      name="entity_id"
                      value={form.entity_id}
                      onChange={updateForm}
                      placeholder="Related order/customer/supplier ID"
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Assigned Owner ID">
                    <input
                      name="owner_id"
                      value={form.owner_id}
                      onChange={updateForm}
                      placeholder="User ObjectId"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <Field label="Description" required>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={updateForm}
                    rows={4}
                    placeholder="Describe the issue, request, or complaint..."
                    className={inputClass}
                    required
                  />
                </Field>

                <Field label="Remarks">
                  <textarea
                    name="remarks"
                    value={form.remarks}
                    onChange={updateForm}
                    rows={3}
                    placeholder="Internal notes or action taken..."
                    className={inputClass}
                  />
                </Field>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={saving}
                    className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-[#f7525a] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#f7525a] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : modal.mode === "create"
                      ? "Create Case"
                      : "Save Changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
